import { z } from 'zod';
import { SlugSchema } from './entities.js';

/**
 * Case Wiki — sortie structurée de la passe de **reconcile sémantique par
 * batch** d'ingestion parallèle.
 *
 * Contexte : après chaque batch de pièces ingérées EN PARALLÈLE, une passe LLM
 * unique examine les faits produits par le batch et identifie les **doublons
 * sémantiques** que le scan SQL du Linter ne voit pas (même fait réel formulé
 * différemment — « Paiement de 15 000 € le 14 juin » vs « versement de la somme
 * de 15.000 euros à la mi-juin »). La passe ne touche PAS la DB : elle renvoie
 * des groupes de doublons que le service applique ensuite via
 * `CaseWikiRepository.mergeFacts` (winner conservé, losers fusionnés dedans).
 *
 * La passe couvre les **deux** problèmes qu'introduit l'ingestion parallèle :
 *   1. doublons sémantiques de faits (`duplicate_groups`) ;
 *   2. contradictions INTRA-BATCH entre faits (`contradictions`) — deux pièces
 *      du même batch qui se contredisent sans s'être vues. Les contradictions
 *      fact-vs-existant sont déjà gérées pendant l'ingest (chaque pièce voit
 *      l'existant via ses read tools), donc seul l'angle intra-batch est traité ici.
 *
 * Les autres anomalies (orphelins, dates manquantes, dédup de parties) ne sont
 * PAS aggravées par le parallélisme : elles restent du ressort du Linter final.
 */

/**
 * Un groupe de faits jugés être le même fait réel. `winner_slug` est le fait à
 * conserver (le plus complet / mieux sourcé) ; chaque `loser_slug` y sera
 * fusionné. `rationale` trace la décision pour audit (faux merge = grave).
 */
export const ReconcileDuplicateGroupSchema = z.object({
  winner_slug: SlugSchema,
  loser_slugs: z.array(SlugSchema).min(1),
  rationale: z.string().min(20).max(500),
});
export type ReconcileDuplicateGroup = z.infer<typeof ReconcileDuplicateGroupSchema>;

/**
 * Une contradiction entre deux faits du batch. Contraintes alignées sur
 * `AppendContradictionPayload` (TitleSchema 10-200, ExplanationSchema 30-2000)
 * pour que la sortie soit directement applicable via `appendContradiction`.
 */
export const ReconcileContradictionSchema = z.object({
  title: z.string().min(10).max(200),
  fact_a_slug: SlugSchema,
  fact_b_slug: SlugSchema,
  explanation: z.string().min(30).max(2000),
});
export type ReconcileContradiction = z.infer<typeof ReconcileContradictionSchema>;

/**
 * Sortie complète de la passe de reconcile d'un batch. `default([])` sur chaque
 * liste : un batch propre renvoie un objet vide valide (le modèle peut omettre
 * les champs).
 */
export const BatchReconcileResultSchema = z.object({
  duplicate_groups: z.array(ReconcileDuplicateGroupSchema).default([]),
  contradictions: z.array(ReconcileContradictionSchema).default([]),
});
export type BatchReconcileResult = z.infer<typeof BatchReconcileResultSchema>;
